<template>
  <div v-if="isOpen" class="modal fade show" style="display: block; background: #1c1d1c33">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="row">
          <div class="col-12">
            <div class="d-flex justify-content-between px-1 my-1 align-item-center">
              <h1 class="mb-0">Asset Transfer</h1>
              <button
                  @click="onClose"
                  type="button"
                  class="btn-close"
                  aria-label="Close"
              ></button>
            </div>
          </div>
        </div>
        <hr class="mt-0">

        <div class="pb-2">
          <div v-if="detailLoading" class="text-center py-3">
            <div class="spinner-border spinner-border-sm" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>

          <div v-if="!detailLoading" class="row gy-2 px-2">
            <div class="col-12">
              <table class="table table-bordered mb-0">
                <thead>
                <tr>
                  <th></th>
                  <th>Current</th>
                  <th>New</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td class="fw-bold align-middle">Project</td>
                  <td class="align-middle">{{ currentLabel(current.project_master_name) }}</td>
                  <td>
                    <AsyncSelect
                        placeholder="Select Project"
                        v-model="form.project_master_id"
                        :api-service="fetchProjects"
                        :format-label="option => option.name"
                        label="name"
                    />
                  </td>
                </tr>
                <tr>
                  <td class="fw-bold align-middle">Cost Centre</td>
                  <td class="align-middle">{{ currentLabel(current.cost_centre_name) }}</td>
                  <td>
                    <v-select
                        placeholder="Select Cost Centre"
                        v-model="form.cost_centre_id"
                        :options="costCentres"
                        :reduce="name => name.id"
                        label="name"
                    />
                  </td>
                </tr>
                <tr>
                  <td class="fw-bold align-middle">Business Unit</td>
                  <td class="align-middle">{{ currentLabel(current.business_name) }}</td>
                  <td>
                    <v-select
                        placeholder="Select Business Unit"
                        v-model="form.business_id"
                        :options="businesses"
                        :reduce="name => name.id"
                        label="name"
                    />
                  </td>
                </tr>
                <tr>
                  <td class="fw-bold align-middle">Custodian</td>
                  <td class="align-middle">{{ currentLabel(current.contact_profile_name) }}</td>
                  <td>
                    <AsyncSelect
                        placeholder="Assign Custodian"
                        v-model="form.contact_profile_id"
                        :api-service="fetchContactProfiles"
                        :additional-query="{type: 'employee'}"
                    />
                  </td>
                </tr>
                <tr>
                  <td class="fw-bold align-middle">Location</td>
                  <td class="align-middle">{{ currentLabel(current.business_location_name) }}</td>
                  <td>
                    <AsyncSelect
                        placeholder="Select Location"
                        v-model="form.business_location_id"
                        :api-service="fetchBusinessLocationsList"
                        label="text"
                        :format-label="option => option.text"
                        :additional-query="{ location_type: 'fixed_assets' }"
                    />
                  </td>
                </tr>
                <tr>
                  <td class="fw-bold align-middle">Capitalization Date</td>
                  <td class="align-middle">{{ currentLabel(current.capitalization_date) }}</td>
                  <td>
                    <input
                        v-model="form.capitalization_date"
                        class="form-control"
                        type="date"
                    >
                  </td>
                </tr>
                </tbody>
              </table>
              <span v-if="error" class="invalid-feedback d-block">{{ error }}</span>
            </div>
          </div>

          <div class="col-12 text-center">
            <hr class="mt-2">
            <button @click="onSubmit" type="button" class="btn btn-primary me-1 mt-1" :disabled="detailLoading">
              <div v-if="saving" class="spinner-border spinner-border-sm text-light" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              Submit
            </button>
            <button @click="onClose" type="button" class="btn btn-outline-secondary mt-1">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, inject } from 'vue'
import { useRoute } from 'vue-router'
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue"
import handleAssetRegister from '@/services/modules/assetRegister/assetRegister'
import handleProjects from "@/services/modules/procurement/project"
import handleContact from "@/services/modules/contact"
import handleBusinessesLocations from '@/services/modules/businessesLocations'
import handleCostCentres from "@/services/modules/procurement/organization-settings/costCentre"
import handleCBusinesses from "@/services/modules/businesses"

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  assetId: { type: [Number, String], default: null }
})

const emit = defineEmits(['onClose', 'onSaved'])

const route = useRoute()

const showError = inject('showError')
const showSuccess = inject('showSuccess')

const { fetchSingleAssetRegister, transferAssetRegister } = handleAssetRegister()
const { fetchProjects } = handleProjects()
const { fetchContactProfiles } = handleContact()
const { fetchBusinessLocationsList } = handleBusinessesLocations()
const { fetchCostCentreList } = handleCostCentres()
const { fetchBusinessList } = handleCBusinesses()

const detailLoading = ref(false)
const saving = ref(false)
const error = ref('')
const current = ref({})
const costCentres = ref([])
const businesses = ref([])

function currentDate() {
  return new Date().toISOString().split('T')[0]
}

const form = ref({
  project_master_id: null,
  cost_centre_id: null,
  business_id: null,
  contact_profile_id: null,
  business_location_id: null,
  capitalization_date: currentDate()
})

function getQuery() {
  return `?company_id=${route.params.companyId}`
}

function currentLabel(value) {
  if (!value) return ''
  return value
}

function resetForm() {
  form.value = {
    project_master_id: null,
    cost_centre_id: null,
    business_id: null,
    contact_profile_id: null,
    business_location_id: null,
    capitalization_date: currentDate()
  }
  error.value = ''
  current.value = {}
}

function loadDropdowns() {
  fetchCostCentreList(getQuery()).then(res => {
    if (res.data) costCentres.value = res.data
  })
  fetchBusinessList(getQuery()).then(res => {
    if (res.data) businesses.value = res.data
  })
}

function loadCurrentAsset() {
  if (!props.assetId) return
  detailLoading.value = true
  fetchSingleAssetRegister(props.assetId, getQuery())
    .then(res => {
      detailLoading.value = false
      if (!res.status) return
      current.value = res.data
    })
    .catch(() => {
      detailLoading.value = false
    })
}

watch(() => props.isOpen, (open) => {
  if (!open) return
  resetForm()
  loadDropdowns()
  loadCurrentAsset()
})

function onClose() {
  resetForm()
  emit('onClose')
}

function onSubmit() {
  if (!form.value.contact_profile_id) {
    error.value = 'Custodian is required'
    return
  }
  if (!form.value.capitalization_date) {
    error.value = 'Capitalization date is required'
    return
  }
  if (!props.assetId) return
  error.value = ''
  saving.value = true
  const data = {
    project_master_id: form.value.project_master_id ? form.value.project_master_id.id : null,
    cost_centre_id: form.value.cost_centre_id,
    business_id: form.value.business_id,
    contact_profile_id: form.value.contact_profile_id.id,
    business_location_id: form.value.business_location_id ? form.value.business_location_id.id : null,
    capitalization_date: form.value.capitalization_date
  }
  transferAssetRegister(data, props.assetId, getQuery())
    .then(res => {
      saving.value = false
      if (!res.status) {
        showError(res.message)
        return
      }
      showSuccess(res.message)
      emit('onSaved')
    })
    .catch(() => {
      saving.value = false
    })
}
</script>
